<script lang="ts">
	//Svelte hooks
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	//Components
	import {
		Label,
		DeviceButton,
		Icon,
		Stack,
		Text,
		DeviceButtonV2,
		LabelV2,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		DeviceButtonPropsTypes,
		DeviceButtonV2PropsTypes,
		DotsPropsTypes,
		LabelV2PropsTypes,
		TextPropsTypes,
	} from '@Shelly/shelly-design-system';
	import type { InputReadersEventsType } from 'src/helpers/input_reader_helpers';
	//Helpers
	import { is_gen_2, isBluRotaryKnob } from '../../../helpers/interface_helpers';
	import { inputStates } from 'src/stores/features/input-states';
	import {
		mappedInputReaderEvent,
		supportsRemoteInputTrigger,
	} from 'src/helpers/input_reader_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import IconForInputReader from '../../../components/EventLog/IconForInputReader.svelte';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import DoubleIconForInputReader from 'src/components/EventLog/DoubleIconForInputReader.svelte';
	import RemoteControlVisualizer from './RemoteControlVisualizer.svelte';

	let className = '';
	export { className as class };
	export let device: Device;
	export let status: { inputs? };
	export let settings: {
		inputs?;
		sys?: { device?: { fw_id?: string } };
		DeviceInfo?: { ver?: string };
	};
	export let isWide: DeviceButtonPropsTypes['isWide'] = false;
	export let isInCard: boolean = true;
	export let showLabel: boolean = false;
	export let useV2Components: boolean = false;
	export let textStyleTyle: Partial<TextPropsTypes> = {};
	export let labelV2Props: Partial<LabelV2PropsTypes> = {};
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 's',
	};

	$: canTriggerInput =
		btnType === 'button' && !!device.virtual_input && supportsRemoteInputTrigger(device, settings);
	$: switchStatusLabel = indicator_status
		? capFirstChar($format('on', { default: 'on' }))
		: capFirstChar($format('off', { default: 'off' }));

	const DeviceButtonManager: { ['v1']: typeof DeviceButton; ['v2']: typeof DeviceButtonV2 } = {
		v1: DeviceButton,
		v2: DeviceButtonV2,
	};

	const mappedEventTexts: InputReadersEventsType = {
		S: $format('event_one_click', { default: 'One click' }),
		SS: $format('event_double_click', { default: 'Double click' }),
		SSS: $format('event_triple_click', { default: 'Triple click' }),
		L: $format('event_long_click', { default: 'Long click' }),
		SL: $format('event_short_long_click', { default: 'Short long click' }),
		LS: $format('event_long_short_click', { default: 'Long short click' }),
	};

	const eventTypes = [
		'',
		'single_push',
		'double_push',
		'triple_push',
		'long_push',
		'btn_down',
		'btn_up',
	];

	$: hasRights = hasControlRights(device);

	let inputColor: DotsPropsTypes['color'] = 'primary';
	let lasteventTimestamp = 0;

	let btnType: string, indicator_status: boolean, event: string, multiple_events: Array<any>;

	let pushType = 0;
	let timer = null;
	let longpress = false;

	// Rotation/scroll wheel data for remote controllers
	let rotationData;
	let buttonEvents = [];

	beforeUpdate(() => {
		const time = +(new Date().getTime() / 1000).toFixed(0);

		if (typeof status?.inputs !== 'undefined') {
			btnType = settings?.inputs?.[device?.channel]?.btn_type;
			indicator_status = status?.inputs?.[device?.channel]?.input === 1;
			// event =
			// 	status?.inputs?.[device?.channel]?.event !== ''
			// 		? status?.inputs?.[device?.channel].event
			// 		: event;
		} else if (is_gen_2(device)) {
			btnType = settings?.['input:' + device?.channel]?.type;
			indicator_status = status?.['input:' + device?.channel]?.state;
			// event = status?.['v_eve:' + device?.channel]?.ev;
		} else if (device.gen == 'GBLE') {
			indicator_status = status?.['input:' + device?.channel]?.state;
			// event = status?.['v_eve:' + device?.channel]?.ev;
			if (Array.isArray($inputStates?.[device.id])) {
				multiple_events = $inputStates?.[device.id]; //since we are displaying only single click event ill pass all of the object to have the time stamp

				// Separate button events from rotation events
				buttonEvents = [];
				rotationData = undefined;

				multiple_events.forEach((evt) => {
					// Check if this is a rotation event (has steps property)
					// Rotation events come from rotinput:0 status key and always have steps
					if (typeof evt.steps === 'number') {
						// This is rotation data
						rotationData = evt;
					} else {
						// This is a button event (v_eve:)
						buttonEvents.push(evt);
					}
				});
			}
		} else {
			indicator_status = false;
		}

		// if (event !== '' && typeof event !== 'undefined') {
		// 	if (
		// 		typeof $inputStates?.[device.id] !== 'undefined' &&
		// 		$inputStates?.[device.id].event !== event
		// 	) {
		// 		inputStates.update(device, status, time);
		// 		lasteventTimestamp = time;
		// 	}

		// 	if (typeof $inputStates?.[device.id] === 'undefined') {
		// 		inputStates.update(device.id, event, time);
		// 		lasteventTimestamp = time;
		// 	}
		// }
		// console.log('$inputStates?.[device.id]', $inputStates?.[device.id]);
		// if (
		// 	typeof $inputStates?.[device.id] !== 'undefined' &&
		// 	(event === '' || typeof event === 'undefined')
		// ) {
		event = $inputStates?.[device.id]?.event;
		lasteventTimestamp = $inputStates?.[device.id]?.time;
		// }

		if (typeof $inputStates?.[device.id] !== 'undefined') {
			if (time - lasteventTimestamp > 10) {
				inputColor = 'default';
				lasteventTimestamp = 0;
			} else {
				inputColor = 'primary';
			}
		}
	});

	const sendInputEvent = async () => {
		if (pushType == 0) return;
		const res = await gen2Request(device, 'Input.Trigger', {
			id: device.channel,
			event_type: eventTypes[pushType],
		});
		if (res.isok) {
			//Todo what to ?? or nothing
		} else {
		}
	};

	$: deviceButtonType = useV2Components ? 'v2' : 'v1';
</script>

{#if btnType === 'toggle' || btnType === 'switch'}
	{#if useV2Components}
		<LabelV2
			class={className}
			value={switchStatusLabel}
			icon="fal fa-power-off"
			size={'l'}
			{...labelV2Props}
		/>
	{:else}
		<Label class={className} value={switchStatusLabel} icon="fas fa-power-off" size="l" />
	{/if}
{:else if isInCard}
	{#if canTriggerInput}
		<svelte:component
			this={DeviceButtonManager[deviceButtonType]}
			id="devicebutton-bfjfyo9"
			aria-label={$format('trigger_input', { default: 'Trigger Input' })}
			{isWide}
			size={useV2Components ? deviceButtonV2Props.size : isWide ? 'l' : 'm'}
			variant="primary"
			isActive={pushType > 0}
			disabled={!hasRights}
			on:click={(e) => {
				if (longpress) {
					pushType = 4;
					sendInputEvent();
					pushType = 0;
					longpress = false;
				} else {
					pushType++;
					clearTimeout(timer);
					timer = setTimeout(() => {
						if (pushType > 3) {
							pushType = 3;
						}
						sendInputEvent();
						pushType = 0;
					}, 500);
				}

				e.preventDefault();
				e.stopPropagation();
				return;
			}}
			on:touchLongpress={async (e) => {
				longpress = true;

				e.preventDefault();
				e.stopPropagation();
			}}
			on:touchstart={(e) => {
				if (longpress) {
					pushType = 4;
					sendInputEvent();
					pushType = 0;
					longpress = false;
				} else {
					pushType++;
					clearTimeout(timer);
					timer = setTimeout(() => {
						if (pushType > 3) {
							pushType = 3;
						}
						sendInputEvent();
						pushType = 0;
					}, 500);
				}
				e.preventDefault();
				e.stopPropagation();
				return;
			}}
		>
			{#if event}
				<IconForInputReader {event} color={'primary'} />
			{:else if buttonEvents?.length > 0 || rotationData}
				<RemoteControlVisualizer
					{buttonEvents}
					{rotationData}
					rotaryOnly={isBluRotaryKnob(device?.type)}
				/>
			{:else}
				<Icon class="width-full text-color-3" icon="fas fa-hand-back-point-up" size="s" />
			{/if}
		</svelte:component>
	{:else if buttonEvents?.length > 0 || rotationData || isBluRotaryKnob(device?.type)}
		<RemoteControlVisualizer
			{buttonEvents}
			{rotationData}
			{useV2Components}
			{labelV2Props}
			rotaryOnly={isBluRotaryKnob(device?.type)}
		/>
	{:else if multiple_events?.length > 1}
		<DoubleIconForInputReader
			event={multiple_events}
			{useV2Components}
			labelProps={{
				size: labelV2Props?.size,
			}}
		/>
	{:else if useV2Components}
		<LabelV2
			class={className}
			value="N/A"
			icon="fal fa-hand-back-point-up"
			dotsType={mappedInputReaderEvent[event]}
			dotsColor={inputColor}
			size={'l'}
			{...labelV2Props}
		/>
	{:else}
		<Label
			class={className}
			value="N/A"
			icon="fas fa-hand-back-point-up"
			dotsType={mappedInputReaderEvent[event]}
			dotsColor={inputColor}
			size="l"
		/>
	{/if}
{:else}
	<Stack gap="xs" align="center" direction="row">
		{#if canTriggerInput}
			<svelte:component
				this={DeviceButtonManager[deviceButtonType]}
				aria-label={$format('trigger_input', { default: 'Trigger Input' })}
				id="devicebutton-qzw6nhb"
				{isWide}
				size={useV2Components ? deviceButtonV2Props.size : isWide ? 'l' : 'm'}
				variant="primary"
				isActive={pushType > 0}
				disabled={!hasRights}
				on:click={(e) => {
					if (longpress) {
						pushType = 4;
						sendInputEvent();
						pushType = 0;
						longpress = false;
					} else {
						pushType++;
						clearTimeout(timer);
						timer = setTimeout(() => {
							if (pushType > 3) {
								pushType = 3;
							}
							sendInputEvent();
							pushType = 0;
						}, 500);
					}

					e.preventDefault();
					e.stopPropagation();
					return;
				}}
				on:touchLongpress={async (e) => {
					longpress = true;

					e.preventDefault();
					e.stopPropagation();
				}}
				on:touchstart={(e) => {
					if (longpress) {
						pushType = 4;
						sendInputEvent();
						pushType = 0;
						longpress = false;
					} else {
						pushType++;
						clearTimeout(timer);
						timer = setTimeout(() => {
							if (pushType > 3) {
								pushType = 3;
							}
							sendInputEvent();
							pushType = 0;
						}, 500);
					}
					e.preventDefault();
					e.stopPropagation();
					return;
				}}
			>
				{#if event}
					<IconForInputReader {event} color={'primary'} />
				{/if}
				{#if buttonEvents?.length > 0 || rotationData}
					<RemoteControlVisualizer
						{buttonEvents}
						{rotationData}
						rotaryOnly={isBluRotaryKnob(device?.type)}
					/>
				{:else}
					<Icon class="width-full text-color-3" icon="fas fa-hand-back-point-up" size="s" />
				{/if}
			</svelte:component>
			<Text styleType={event ? 'tiny' : 'h1'} class="text-center" {...textStyleTyle}>
				{mappedEventTexts[event] || 'N/A'}
			</Text>
		{:else if buttonEvents?.length > 0 || rotationData}
			<RemoteControlVisualizer
				{buttonEvents}
				{rotationData}
				{useV2Components}
				{labelV2Props}
				rotaryOnly={isBluRotaryKnob(device?.type)}
			/>
		{:else if typeof multiple_events !== 'undefined'}
			<DoubleIconForInputReader event={multiple_events} />
		{:else if event}
			<IconForInputReader {event} color={inputColor} />
			<Icon class="width-full" icon="fas fa-hand-back-point-up" size="s" />
			<Text styleType={event ? 'tiny' : 'h1'} {...textStyleTyle}>
				{mappedEventTexts[event] || 'N/A'}
			</Text>
		{:else}
			<Icon class="width-full" icon="fas fa-hand-back-point-up" size="s" />
			<Text styleType="h1" {...textStyleTyle}>N/A</Text>
		{/if}
	</Stack>
{/if}
